import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import { GroupBuyProviderSchema } from "./commercial.js";
import {
  deriveGroupBuyReconciliationNumber,
  GroupBuyReconciliationNumberSchema,
} from "./group-buy-reconciliation-number.js";

const StableIdSchema = Type.String({
  minLength: 12,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$",
});
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: 100_000_000 });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 2, maxLength: maximum, pattern: CanonicalTextPattern });
const PlatformReferenceSchema = Type.String({
  minLength: 4,
  maxLength: 64,
  pattern: "^[^\\s]+$",
});
const ExplanationListSchema = Type.Array(CanonicalTextSchema(300), {
  minItems: 1,
  maxItems: 8,
  uniqueItems: true,
});

export const GroupBuyStatementLineSchema = Type.Object(
  {
    lineNumber: Type.Integer({ minimum: 1, maximum: 500 }),
    platformReference: PlatformReferenceSchema,
    actualSettlementFen: AmountFenSchema,
    actualFeeFen: AmountFenSchema,
    actualSubsidyFen: AmountFenSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyStatementLine = Static<typeof GroupBuyStatementLineSchema>;

const ReconciliationRequestFields = {
  reconciliationId: StableIdSchema,
  businessDate: BusinessDateSchema,
  provider: GroupBuyProviderSchema,
  statementReference: CanonicalTextSchema(80),
  statementLines: Type.Array(GroupBuyStatementLineSchema, { maxItems: 500 }),
  note: CanonicalTextSchema(500),
};

export const PreviewGroupBuyDailyReconciliationRequestSchema = Type.Object(
  ReconciliationRequestFields,
  { additionalProperties: false },
);
export type PreviewGroupBuyDailyReconciliationRequest = Static<
  typeof PreviewGroupBuyDailyReconciliationRequestSchema
>;

export const GroupBuyDailyReconciliationOutcomeSchema = Type.Union([
  Type.Literal("CONFIRMED"),
  Type.Literal("NOT_SETTLED"),
  Type.Literal("AMOUNT_MISMATCH"),
  Type.Literal("DUPLICATE_RECORD"),
]);
export type GroupBuyDailyReconciliationOutcome = Static<
  typeof GroupBuyDailyReconciliationOutcomeSchema
>;

export const GroupBuyDailyReconciliationMatchSchema = Type.Object(
  {
    fulfillmentId: StableIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    packageName: Type.String({ minLength: 1, maxLength: 80 }),
    platformReference: PlatformReferenceSchema,
    expectedSettlementFen: AmountFenSchema,
    expectedFeeFen: AmountFenSchema,
    expectedSubsidyFen: AmountFenSchema,
    actualSettlementFen: AmountFenSchema,
    actualFeeFen: AmountFenSchema,
    actualSubsidyFen: AmountFenSchema,
    statementLineNumbers: Type.Array(Type.Integer({ minimum: 1, maximum: 500 }), {
      maxItems: 500,
      uniqueItems: true,
    }),
    outcome: GroupBuyDailyReconciliationOutcomeSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyDailyReconciliationMatch = Static<
  typeof GroupBuyDailyReconciliationMatchSchema
>;

export const GroupBuyDailyReconciliationPreviewSchema = Type.Object(
  {
    kind: Type.Literal("GROUP_BUY_DAILY_RECONCILIATION"),
    reconciliationId: StableIdSchema,
    reconciliationNumber: GroupBuyReconciliationNumberSchema,
    businessDate: BusinessDateSchema,
    provider: GroupBuyProviderSchema,
    statementReference: CanonicalTextSchema(80),
    statementLines: Type.Array(GroupBuyStatementLineSchema, { maxItems: 500 }),
    matches: Type.Array(GroupBuyDailyReconciliationMatchSchema, { maxItems: 500 }),
    unmatchedStatementLines: Type.Array(GroupBuyStatementLineSchema, { maxItems: 500 }),
    fulfillmentCount: Type.Integer({ minimum: 0, maximum: 500 }),
    confirmedCount: Type.Integer({ minimum: 0, maximum: 500 }),
    exceptionCount: Type.Integer({ minimum: 0, maximum: 1_000 }),
    expectedSettlementFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    actualSettlementFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    expectedFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    actualFeeFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    expectedSubsidyFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    actualSubsidyFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    note: CanonicalTextSchema(500),
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    originalDocumentLabel: CanonicalTextSchema(120),
    consequences: ExplanationListSchema,
    nextSteps: ExplanationListSchema,
    requirement: Type.Literal("MANAGER_OR_OWNER"),
    approverLabel: CanonicalTextSchema(120),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyDailyReconciliationPreview = Static<
  typeof GroupBuyDailyReconciliationPreviewSchema
>;

export const CreateGroupBuyDailyReconciliationApprovalCommandSchema = Type.Object(
  {
    ...ReconciliationRequestFields,
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CreateGroupBuyDailyReconciliationApprovalCommand = Static<
  typeof CreateGroupBuyDailyReconciliationApprovalCommandSchema
>;

export const GroupBuyDailyReconciliationApprovalSnapshotSchema = Type.Object(
  {
    ...GroupBuyDailyReconciliationPreviewSchema.properties,
    snapshotVersion: Type.Literal(1),
    operationTrace: BusinessOperationTraceV1Schema,
    commandSha256: Sha256Schema,
    capturedAt: TimestampSchema,
    orderEffect: Type.Literal("NONE"),
    inventoryEffect: Type.Literal("NONE"),
    memberBalanceEffect: Type.Literal("NONE"),
    moneyLedgerEffect: Type.Literal("NONE"),
  },
  { additionalProperties: false },
);
export type GroupBuyDailyReconciliationApprovalSnapshot = Static<
  typeof GroupBuyDailyReconciliationApprovalSnapshotSchema
>;

export const GroupBuyDailyReconciliationRecordSchema = Type.Object(
  {
    reconciliationId: StableIdSchema,
    reconciliationNumber: GroupBuyReconciliationNumberSchema,
    approvalId: StableIdSchema,
    snapshot: GroupBuyDailyReconciliationApprovalSnapshotSchema,
    applicantActorId: StableIdSchema,
    authorizerActorId: StableIdSchema,
    status: Type.Literal("COMPLETED"),
    completedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type GroupBuyDailyReconciliationRecord = Static<
  typeof GroupBuyDailyReconciliationRecordSchema
>;

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function requestIsSemanticallyValid(value: PreviewGroupBuyDailyReconciliationRequest): boolean {
  return (
    isCanonicalBusinessDate(value.businessDate) &&
    new Set(value.statementLines.map((line) => line.lineNumber)).size ===
      value.statementLines.length
  );
}

export function isPreviewGroupBuyDailyReconciliationRequest(
  value: unknown,
): value is PreviewGroupBuyDailyReconciliationRequest {
  return (
    Value.Check(PreviewGroupBuyDailyReconciliationRequestSchema, value) &&
    requestIsSemanticallyValid(value as PreviewGroupBuyDailyReconciliationRequest)
  );
}

export function isGroupBuyDailyReconciliationPreview(
  value: unknown,
): value is GroupBuyDailyReconciliationPreview {
  if (!Value.Check(GroupBuyDailyReconciliationPreviewSchema, value)) return false;
  return previewIsSemanticallyValid(value as GroupBuyDailyReconciliationPreview);
}

function safeSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

function previewIsSemanticallyValid(preview: GroupBuyDailyReconciliationPreview): boolean {
  const expectedNumber = deriveGroupBuyReconciliationNumber(
    preview.businessDate,
    preview.reconciliationId,
  );
  const matchedIds = new Set(preview.matches.map((line) => line.fulfillmentId));
  const matchedReferences = new Set(preview.matches.map((line) => line.platformReference));
  const statementLineNumbers = new Set(preview.statementLines.map((line) => line.lineNumber));
  const statementLinesByNumber = new Map(
    preview.statementLines.map((line) => [line.lineNumber, line]),
  );
  const referencedLineNumberList = preview.matches.flatMap((line) => line.statementLineNumbers);
  const referencedLineNumbers = new Set(referencedLineNumberList);
  const unmatchedLineNumbers = new Set(
    preview.unmatchedStatementLines.map((line) => line.lineNumber),
  );
  const expectedSettlementFen = safeSum(preview.matches.map((line) => line.expectedSettlementFen));
  const expectedFeeFen = safeSum(preview.matches.map((line) => line.expectedFeeFen));
  const expectedSubsidyFen = safeSum(preview.matches.map((line) => line.expectedSubsidyFen));
  const actualSettlementFen = safeSum(
    preview.statementLines.map((line) => line.actualSettlementFen),
  );
  const actualFeeFen = safeSum(preview.statementLines.map((line) => line.actualFeeFen));
  const actualSubsidyFen = safeSum(preview.statementLines.map((line) => line.actualSubsidyFen));
  return (
    requestIsSemanticallyValid(preview) &&
    isCanonicalTimestamp(preview.generatedAt) &&
    preview.reconciliationNumber === expectedNumber &&
    matchedIds.size === preview.matches.length &&
    matchedReferences.size === preview.matches.length &&
    preview.fulfillmentCount === preview.matches.length &&
    preview.confirmedCount ===
      preview.matches.filter((line) => line.outcome === "CONFIRMED").length &&
    preview.exceptionCount ===
      preview.matches.filter((line) => line.outcome !== "CONFIRMED").length +
        preview.unmatchedStatementLines.length &&
    referencedLineNumbers.size === referencedLineNumberList.length &&
    unmatchedLineNumbers.size === preview.unmatchedStatementLines.length &&
    referencedLineNumbers.size + unmatchedLineNumbers.size === statementLineNumbers.size &&
    [...referencedLineNumbers].every(
      (lineNumber) => statementLineNumbers.has(lineNumber) && !unmatchedLineNumbers.has(lineNumber),
    ) &&
    preview.unmatchedStatementLines.every(
      (line) =>
        JSON.stringify(statementLinesByNumber.get(line.lineNumber)) === JSON.stringify(line),
    ) &&
    preview.matches.every((match) => {
      const statementLines = match.statementLineNumbers.map((lineNumber) =>
        statementLinesByNumber.get(lineNumber),
      );
      if (statementLines.some((line) => line?.platformReference !== match.platformReference)) {
        return false;
      }
      if (statementLines.length === 0) {
        return (
          match.outcome === "NOT_SETTLED" &&
          match.actualSettlementFen === 0 &&
          match.actualFeeFen === 0 &&
          match.actualSubsidyFen === 0
        );
      }
      const settlement = safeSum(statementLines.map((line) => line?.actualSettlementFen ?? -1));
      const fee = safeSum(statementLines.map((line) => line?.actualFeeFen ?? -1));
      const subsidy = safeSum(statementLines.map((line) => line?.actualSubsidyFen ?? -1));
      if (
        settlement === null ||
        fee === null ||
        subsidy === null ||
        settlement !== match.actualSettlementFen ||
        fee !== match.actualFeeFen ||
        subsidy !== match.actualSubsidyFen
      ) {
        return false;
      }
      if (statementLines.length > 1) return match.outcome === "DUPLICATE_RECORD";
      const amountsMatch =
        match.actualSettlementFen === match.expectedSettlementFen &&
        match.actualFeeFen === match.expectedFeeFen &&
        match.actualSubsidyFen === match.expectedSubsidyFen;
      return match.outcome === (amountsMatch ? "CONFIRMED" : "AMOUNT_MISMATCH");
    }) &&
    expectedSettlementFen === preview.expectedSettlementFen &&
    expectedFeeFen === preview.expectedFeeFen &&
    expectedSubsidyFen === preview.expectedSubsidyFen &&
    actualSettlementFen === preview.actualSettlementFen &&
    actualFeeFen === preview.actualFeeFen &&
    actualSubsidyFen === preview.actualSubsidyFen &&
    preview.canSubmit === (preview.fulfillmentCount > 0 || preview.statementLines.length > 0) &&
    (preview.canSubmit ? preview.blocker === null : preview.blocker !== null)
  );
}

export function isCreateGroupBuyDailyReconciliationApprovalCommand(
  value: unknown,
): value is CreateGroupBuyDailyReconciliationApprovalCommand {
  if (!Value.Check(CreateGroupBuyDailyReconciliationApprovalCommandSchema, value)) return false;
  const command = value as CreateGroupBuyDailyReconciliationApprovalCommand;
  const expectedNumber = deriveGroupBuyReconciliationNumber(
    command.businessDate,
    command.reconciliationId,
  );
  return (
    requestIsSemanticallyValid(command) &&
    isBusinessOperationTraceV1(command.operationTrace) &&
    command.operationTrace.idempotencyKey === command.idempotencyKey &&
    command.operationTrace.correlationId ===
      `group-buy-reconciliation:${command.reconciliationId}` &&
    command.operationTrace.businessNumber?.kind === "GROUP_BUY_RECONCILIATION" &&
    command.operationTrace.businessNumber.value === expectedNumber
  );
}

export function isGroupBuyDailyReconciliationApprovalSnapshot(
  value: unknown,
): value is GroupBuyDailyReconciliationApprovalSnapshot {
  if (!Value.Check(GroupBuyDailyReconciliationApprovalSnapshotSchema, value)) return false;
  const snapshot = value as GroupBuyDailyReconciliationApprovalSnapshot;
  return (
    previewIsSemanticallyValid(snapshot) &&
    isCanonicalTimestamp(snapshot.capturedAt) &&
    Date.parse(snapshot.capturedAt) >= Date.parse(snapshot.generatedAt) &&
    isBusinessOperationTraceV1(snapshot.operationTrace) &&
    snapshot.operationTrace.correlationId ===
      `group-buy-reconciliation:${snapshot.reconciliationId}` &&
    snapshot.operationTrace.businessNumber?.kind === "GROUP_BUY_RECONCILIATION" &&
    snapshot.operationTrace.businessNumber.value === snapshot.reconciliationNumber
  );
}

export function isGroupBuyDailyReconciliationRecord(
  value: unknown,
): value is GroupBuyDailyReconciliationRecord {
  if (!Value.Check(GroupBuyDailyReconciliationRecordSchema, value)) return false;
  const record = value as GroupBuyDailyReconciliationRecord;
  return (
    isGroupBuyDailyReconciliationApprovalSnapshot(record.snapshot) &&
    isCanonicalTimestamp(record.completedAt) &&
    record.reconciliationId === record.snapshot.reconciliationId &&
    record.reconciliationNumber === record.snapshot.reconciliationNumber &&
    record.applicantActorId !== record.authorizerActorId &&
    Date.parse(record.completedAt) >= Date.parse(record.snapshot.capturedAt)
  );
}
